/**
 * Lane C0-FACE (lab only; nothing in the game imports this). The face parts of the proposed face system besides
 * the eyes and brows (those are the traced C0 features with more variants and expression steps: fine.ts), drawn
 * at the rig's full resolution (two canvas pixels per art pixel, the grid of head2.ts): noses (small stamps per
 * view), mouths (a rest stamp; expression mouths are built to its width), jaws (skin pixels added to the shared
 * head at art resolution) and marks (scars, moles, wrinkles).
 *
 * Coordinates: canvas pixels from the head joint's art pixel (as head2.ts). Head-on the face's centre line is
 * between x −1 and 0.
 * Research: docs/research/C0_FACES.md.
 */
import type { View } from '../../sprites';

export type Sex = 'male' | 'female';
export type Age = 'young' | 'adult' | 'elder';
export const EXPRS = ['neutral', 'smile', 'talk', 'angry', 'shout', 'hurt', 'blink'] as const;
export type Expr = (typeof EXPRS)[number];
export const EXPR_LABELS: Record<Expr, string> = {
  neutral: 'ปกติ',
  smile: 'ยิ้ม',
  talk: 'พูด',
  angry: 'โกรธ/ตั้งท่า',
  shout: 'ตะโกนโจมตี',
  hurt: 'เจ็บ',
  blink: 'กะพริบ/หลับตา',
};

export interface NoseShape {
  id: string;
  label: string;
  /** Per view: a stamp of `s` (shade), `S` (deep shade) and `H` (light) on the skin. */
  views: Partial<Record<View, { x: number; y: number; rows: readonly string[] }>>;
}

export interface MouthShape {
  id: string;
  label: string;
  /** The mouth at rest head-on: `m` lip line, `n` its soft ends, `L` lip colour, `M` open mouth, `t` teeth. */
  rows: readonly string[];
  /** Rows of lip colour for women in expression mouths. */
  lips?: boolean;
}

/** Skin added to the shared head (art pixels: head-grid column, row; heads.ts HEAD), mirrored head-on. */
export interface JawShape {
  id: string;
  label: string;
  add: Partial<Record<View, readonly [number, number][]>>;
}

// ── noses ──

export const NOSES: readonly NoseShape[] = [
  {
    id: 'dot',
    label: 'จมูกจุด',
    views: {
      front: { x: -1, y: 12, rows: ['s'] },
      front3: { x: 7, y: 12, rows: ['s.', '.s'] },
      side: { x: 15, y: 12, rows: ['s'] },
    },
  },
  {
    id: 'button',
    label: 'จมูกเล็กปลายมน',
    views: {
      front: { x: -2, y: 11, rows: ['.H.', 's.s'] },
      front3: { x: 6, y: 11, rows: ['.sH', 'ss.'] },
      side: { x: 14, y: 12, rows: ['s'] },
    },
  },
  {
    id: 'bridge',
    label: 'จมูกโด่ง (สันจมูก)',
    views: {
      front: { x: -1, y: 8, rows: ['s.', 's.', 's.', 'sH', 'ss'] },
      front3: { x: 6, y: 8, rows: ['s..', 's..', '.s.', '.sH', 'ss.'] },
      side: { x: 14, y: 12, rows: ['ss'] },
    },
  },
  {
    id: 'broad',
    label: 'จมูกกว้าง',
    views: {
      front: { x: -3, y: 11, rows: ['..H.', 's..s', '.ss.'] },
      front3: { x: 5, y: 11, rows: ['..sH', 'ss.s', '.ss.'] },
      side: { x: 13, y: 12, rows: ['sss'] },
    },
  },
  {
    id: 'hook',
    label: 'จมูกงุ้ม',
    views: {
      front: { x: -1, y: 9, rows: ['s.', 's.', 'sH', 'Ss', '.s'] },
      front3: { x: 6, y: 9, rows: ['s..', '.s.', '.sH', 'SSs'] },
      side: { x: 14, y: 12, rows: ['Ss'] },
    },
  },
];

// ── mouths ──

export const MOUTHS: readonly MouthShape[] = [
  // Women.
  { id: 'petal', label: 'ปากเล็กแดง', rows: ['mm', 'LL'], lips: true },
  { id: 'bow', label: 'ปากกระจับ', rows: ['nmmn', '.LL.'], lips: true },
  { id: 'smileF', label: 'ยิ้มมุมปาก (หญิง)', rows: ['m..m', '.mm.'], lips: true },
  { id: 'pout', label: 'ปากอิ่ม', rows: ['.mm.', 'LLLL'], lips: true },
  // Men.
  { id: 'line', label: 'ปากเส้นตรง', rows: ['nmmn'] },
  { id: 'firm', label: 'ปากเม้มแน่น', rows: ['nmmmn'] },
  { id: 'frown', label: 'ปากคว่ำ', rows: ['.mmm.', 'n...n'] },
  { id: 'smirk', label: 'ยิ้มเยาะ', rows: ['...n', 'nmm.'] },
  { id: 'grin', label: 'ยิ้มกว้างใจดี', rows: ['m...m', '.mmm.'] },
  { id: 'small', label: 'ปากเล็ก', rows: ['mm'] },
];

// ── jaws ──

export const JAWS: readonly JawShape[] = [
  { id: 'soft', label: 'กรามมน (เดิม)', add: {} },
  {
    id: 'square',
    label: 'กรามเหลี่ยม',
    add: {
      front: [
        [2, 16],
        [3, 17],
        [4, 18],
        [5, 19],
        [6, 19],
      ],
      front3: [
        [3, 16],
        [4, 17],
        [5, 18],
        [7, 19],
        [13, 19],
      ],
      side: [
        [5, 16],
        [5, 17],
        [6, 17],
        [6, 18],
        [8, 19],
      ],
    },
  },
  {
    id: 'firm',
    label: 'คางชัด',
    add: {
      front: [
        [3, 17],
        [6, 19],
      ],
      front3: [
        [4, 17],
        [13, 19],
      ],
      side: [
        [6, 18],
        [16, 18],
      ],
    },
  },
  {
    id: 'round',
    label: 'แก้มป่อง',
    add: {
      front: [
        [1, 14],
        [1, 15],
        [2, 16],
        [3, 17],
      ],
      front3: [
        [2, 14],
        [2, 15],
        [3, 16],
        [16, 15],
      ],
      side: [[17, 15]],
    },
  },
  {
    id: 'broad',
    label: 'หน้ากว้างล่ำ',
    add: {
      front: [
        [1, 14],
        [1, 15],
        [2, 16],
        [3, 17],
        [4, 18],
        [5, 19],
        [6, 19],
      ],
      front3: [
        [2, 14],
        [2, 15],
        [3, 16],
        [4, 17],
        [5, 18],
        [7, 19],
        [13, 19],
      ],
      side: [
        [5, 16],
        [5, 17],
        [6, 17],
        [6, 18],
        [8, 19],
      ],
    },
  },
];

// ── marks ──

/** Marks at full resolution: `x` scar (pale, raised), `X` scar shade, `o` mole, `c` wrinkle (skin shade). */
export interface MarkShape {
  id: string;
  label: string;
  views: Partial<Record<View, readonly { x: number; y: number; rows: readonly string[] }[]>>;
  /**
   * On one side of the face: where the mirror image's mark lies on the left-facing views (rig/leftArt.ts), per view
   * (an empty list: out of sight there). A view left out keeps `views`.
   */
  left?: Partial<Record<View, readonly { x: number; y: number; rows: readonly string[] }[]>>;
}

export const MARKS: readonly MarkShape[] = [
  {
    id: 'scarBrow',
    label: 'แผลเป็นคิ้ว',
    views: {
      front: [{ x: 7, y: -2, rows: ['..x', '.xX', 'xX.', 'X..', ...Array<string>(9).fill('...'), '.x.', 'x..'] }],
      front3: [{ x: 10, y: -2, rows: ['.x', 'xX', 'X.', ...Array<string>(10).fill('..'), 'x.'] }],
      side: [{ x: 9, y: -2, rows: ['.x', 'xX', 'X.'] }],
    },
    // Through the left brow: on the mirror image, the near brow three-quarter on (rig/leftArt.ts).
    left: { front3: [{ x: -2, y: -2, rows: ['..x', '.xX', 'xX.', 'X..', ...Array<string>(9).fill('...'), '.x.', 'x..'] }] },
  },
  {
    id: 'scarCheek',
    label: 'แผลเป็นแก้ม',
    views: {
      front: [{ x: -11, y: 11, rows: ['x...', 'Xx..', '.Xx.', '..Xx'] }],
      front3: [{ x: -7, y: 11, rows: ['x..', 'Xx.', '.Xx'] }],
      side: [{ x: 6, y: 11, rows: ['x...', 'Xx..', '.Xx.', '..Xx'] }],
    },
    // On the right cheek: on the mirror image, the far cheek three-quarter on, foreshortened; side-on out of sight.
    left: { front3: [{ x: 10, y: 11, rows: ['.x', 'xX', 'X.'] }], side: [] },
  },
  {
    id: 'mole',
    label: 'ไฝ',
    views: { front: [{ x: 9, y: 13, rows: ['o'] }], front3: [{ x: 11, y: 13, rows: ['o'] }], side: [{ x: 10, y: 13, rows: ['o'] }] },
    // On the left cheek: on the mirror image, the near cheek three-quarter on.
    left: { front3: [{ x: -4, y: 13, rows: ['o'] }] },
  },
  {
    id: 'brow',
    label: 'รอยย่นหว่างคิ้ว',
    views: { front: [{ x: -2, y: 1, rows: ['c..c', '.cc.'] }], front3: [{ x: 4, y: 1, rows: ['c.c', '.c.'] }] },
  },
  {
    id: 'forehead',
    label: 'รอยย่นหน้าผาก',
    views: {
      front: [{ x: -5, y: -4, rows: ['.cccccccc.', '..........', 'cc......cc'] }],
      front3: [{ x: -2, y: -4, rows: ['.cccccc', '.......', 'cc....c'] }],
    },
  },
];

export const byId = <T extends { id: string }>(list: readonly T[], id: string): T =>
  list.find((p) => p.id === id) ?? list[0]!;
